/**
 * B12 / §6.1 — adds every step of a ritual in one request.
 *
 * /cart/add.js accepts an `items` array, so the whole ritual is one atomic call rather
 * than N sequential ones: a partial ritual in the cart is worse than a clear failure.
 *
 * The exit criterion for B12 is "adding a bundle adds all items", so the button reports
 * how many lines actually landed rather than assuming success.
 */

const CART_LINES_UPDATE = 'shopify:cart:lines-update';

/** Variant ids come from the steps' data attributes; unset or sold-out steps carry none. */
export function parseVariantIds(raw) {
  return String(raw || '')
    .split(',')
    .map((id) => id.trim())
    .filter((id) => /^\d+$/.test(id));
}

/**
 * Format minor units with the shop's own money_format, so the total matches the prices
 * rendered beside it. Mirrors the substitution Shopify's own scripts do — using a bare
 * Intl currency format would print "₹18,899.00" next to the theme's "Rs. 18,899.00".
 */
export function formatMoney(cents, format) {
  const amount = Number(cents) || 0;
  const group = (value, sep) => value.replace(/\B(?=(\d{3})+(?!\d))/g, sep);

  const variants = {
    amount: () => `${group((amount / 100).toFixed(2).split('.')[0], ',')}.${(amount / 100).toFixed(2).split('.')[1]}`,
    amount_no_decimals: () => group(String(Math.round(amount / 100)), ','),
    amount_with_comma_separator: () =>
      `${group((amount / 100).toFixed(2).split('.')[0], '.')},${(amount / 100).toFixed(2).split('.')[1]}`,
    amount_no_decimals_with_comma_separator: () => group(String(Math.round(amount / 100)), '.'),
  };

  if (!format || !/\{\{\s*\w+\s*\}\}/.test(format)) {
    return variants.amount();
  }

  return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) =>
    variants[key] ? variants[key]() : variants.amount()
  );
}

if (typeof customElements !== 'undefined' && !customElements.get('ritual-bundle-form')) {
  class RitualBundleForm extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-ref="add"]');
      this.status = this.querySelector('[data-ref="status"]');
      this.totalOut = this.querySelector('[data-ref="total"]');
      this.root = this.closest('.ritual-bundle');

      this.collect();
      this.button?.addEventListener('click', () => this.add());
    }

    /**
     * Steps publish their own variant id and price; only purchasable ones carry them, so
     * an unset or sold-out step is excluded from both the total and the add payload.
     */
    collect() {
      const steps = this.root ? [...this.root.querySelectorAll('.ritual-step[data-variant-id]')] : [];
      this.ids = steps.map((el) => el.dataset.variantId).filter(Boolean);

      const total = steps.reduce((sum, el) => sum + (Number(el.dataset.price) || 0), 0);
      if (this.totalOut) {
        this.totalOut.textContent = formatMoney(total, this.root?.dataset.moneyFormat);
      }

      // Nothing purchasable — a footer offering to add nothing is worse than no footer.
      this.hidden = this.ids.length === 0;
    }

    async add() {
      const ids = this.ids ?? [];
      if (!ids.length) return;

      const original = this.button.textContent.trim();
      this.button.disabled = true;
      this.setStatus('');

      try {
        const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: ids.map((id) => ({ id: Number(id), quantity: 1 })) }),
        });

        const payload = await response.json();
        if (!response.ok) {
          // Shopify returns a human-readable reason (out of stock, limit reached).
          throw new Error(payload?.description || payload?.message || 'Could not add the ritual.');
        }

        const added = payload?.items?.length ?? ids.length;
        this.button.textContent = 'Added';
        this.setStatus(`${added} ${added === 1 ? 'item' : 'items'} added to cart`);
        this.notifyCart();

        setTimeout(() => {
          this.button.textContent = original;
        }, 2400);
      } catch (error) {
        this.setStatus(error.message, 'error');
      } finally {
        this.button.disabled = false;
      }
    }

    /**
     * The cart icon and drawer listen for the theme's standard event and read
     * `event.promise`. Resolve it from a real /cart.js read rather than guessing a count,
     * so the bubble cannot drift from the actual cart.
     */
    notifyCart() {
      const promise = fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`)
        .then((res) => res.json())
        .then((cart) => ({
          cart: { totalQuantity: cart.item_count },
          detail: { itemCount: cart.item_count },
        }))
        .catch(() => ({ cart: null, detail: { itemCount: 0 } }));

      const event = new CustomEvent(CART_LINES_UPDATE, { bubbles: true });
      event.promise = promise;
      document.dispatchEvent(event);
    }

    setStatus(message, state) {
      if (!this.status) return;
      this.status.textContent = message;
      if (state) {
        this.status.dataset.state = state;
      } else {
        delete this.status.dataset.state;
      }
    }
  }

  customElements.define('ritual-bundle-form', RitualBundleForm);
}
